import React, { useEffect, useLayoutEffect, lazy, Suspense, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import logo from '../../sidebar-logo.png';
import mixpanel from 'mixpanel-browser';
import {
  School as SchoolIcon,
  Work as WorkIcon,
  Accessibility as AccessibilityIcon,
  AutoAwesome as AutoAwesomeIcon,
  People as PeopleIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { setLandingPage } from '../../utils/trackingParams';
import { logConversion } from '../../firebase';
import userStore from '../../Stores/User';
import { isPaidProUser, hasPaidPlan } from '../../constants/plans';
import './landingA.scss';

const Testimonials = lazy(() => import('./Components/Testimonials'));
const requestIdleCallback = window.requestIdleCallback || ((callback) => setTimeout(callback, 1));

const ProBadge = observer(() => {
  const isProUser = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id ? isPaidProUser(userStore.user) : false;
  const hasPlan = hasPaidPlan(userStore.user);
  const [showPopover, setShowPopover] = React.useState(false);
  const badgeStyle = {
    background: isProUser ? 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)' : hasPlan ? 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)' : 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)',
    padding: '2px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', position: 'absolute', top: isProUser ? '35px' : '0px', left: '45px',
    display: 'inline-flex', alignItems: 'center', height: '16px', width: isProUser ? '32px' : hasPlan ? '70px' : '95px', justifyContent: 'center',
    cursor: isProUser ? 'default' : 'pointer', textDecoration: 'none', whiteSpace: 'nowrap', color: 'white'
  };
  const popoverStyle = { position: 'absolute', top: '25px', left: '45px', background: 'rgba(0, 0, 0, 0.9)', padding: '8px 12px', borderRadius: '8px',
    fontSize: '12px', width: '200px', color: 'white', zIndex: 1000, pointerEvents: 'none', opacity: showPopover ? 1 : 0,
    transition: 'opacity 0.2s ease-in-out', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)', backdropFilter: 'blur(5px)'
  };
  if (!userStore.user) return null;
  if (isProUser) return <div style={badgeStyle}>PRO</div>;
  if (hasPlan) return (
    <div style={{ position: 'relative' }}>
      <a href={userStore.customerPortalUrl} target="_blank" rel="noreferrer" style={badgeStyle} onMouseEnter={() => setShowPopover(true)} onMouseLeave={() => setShowPopover(false)}>UPGRADE</a>
      <div style={popoverStyle}>Upgrade to pro to get premium voices, other languages, and more!</div>
    </div>
  );
  return (
    <div style={{ position: 'relative' }}>
      <Link to="/onboarding/ChooseVoice" style={badgeStyle} onMouseEnter={() => setShowPopover(true)} onMouseLeave={() => setShowPopover(false)}>START TRIAL</Link>
      <div style={popoverStyle}>Start your 7-day free trial today!</div>
    </div>
  );
});

const LandingSolutions = observer(() => {
  const cta = "Start listening";
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  useLayoutEffect(() => {
    // Scroll to top immediately before paint
    window.scrollTo(0, 0);
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    requestIdleCallback(() => {
      mixpanel.track('Solutions Landing Page Visited', { page: 'landing_solutions' });
      setLandingPage('landing_solutions');
    });
  }, []);

  const renderSignUpButton = useCallback(() => {
    return (
      <div className="cta-div" style={{ position: 'static', width: 'auto' }}>
        <Link to="/signin?source=nav_signup&redirected_from=solutions" className="cta-button"
          onClick={() => setTimeout(() => { mixpanel.track('Click Sign Up (Nav)', { placement: 'Solutions' }); logConversion('sign_up_click', 'A'); }, 0)}>
          <div style={{ display: 'flex', justifyContent: 'space-around' }}><div><div>{cta}</div></div></div>
        </Link>
      </div>
    );
  }, [cta]);

  const renderCta = useCallback(() => {
    return (
      <div className="cta-div" style={{ position: 'static' }}>
        <Link to="/signin?source=solutions&redirected_from=solutions" className="cta-button"
          onClick={() => setTimeout(() => { mixpanel.track('Click Try For Free', { placement: 'Solutions' }); logConversion('try_for_free_click', 'A'); }, 0)}>
          <div style={{ display: 'flex', justifyContent: 'space-around' }}><div><div>Start Listening Now</div></div></div>
        </Link>
      </div>
    );
  }, []);

  return (
    <div className="App" style={{ contain: 'layout style paint' }}>
      <Helmet>
        <title>Learn, Focus & Thrive with AI Audio | Audioread Solutions</title>
        <meta name="description" content="Turn study notes, articles, or reports into audio and learn on the go. Boost focus, accessibility, and productivity with AI-powered sound." />
        <meta property="og:title" content="Learn, Focus & Thrive with AI Audio | Audioread Solutions" />
        <meta property="og:description" content="Turn study notes, articles, or reports into audio and learn on the go. Boost focus, accessibility, and productivity with AI-powered sound." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content="https://audioread.com/logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Learn, Focus & Thrive with AI Audio | Audioread Solutions" />
        <meta name="twitter:description" content="Turn study notes, articles, or reports into audio and learn on the go. Boost focus, accessibility, and productivity with AI-powered sound." />
        <meta name="twitter:image" content="https://audioread.com/logo.png" />
      </Helmet>
      <link rel="preload" href={logo} as="image" />
      
      <header className="App-header" style={{ minHeight: '100vh' }}>
        <div className="nav-bar">
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <div style={{ position: "relative" }} className="logo-container">
              <Link to="/"><img className="audiblogs-logo" src={logo} alt="logo" loading="eager" decoding="sync" style={{ contain: 'layout style paint' }} /></Link>
              <ProBadge />
            </div>
          </div>
          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '20px', fontSize: '1.05rem', fontWeight: 'bold' }}>
              <Link className="signin-button" to="/signin?source=nav_signin&redirected_from=solutions">Sign In</Link>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', transform: 'scale(0.75)', marginLeft: '-20px'}}>
              {renderSignUpButton()}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  marginLeft: '5px',
                  width: '44px',
                  height: '44px',
                  minWidth: '44px',
                  minHeight: '44px',
                  flexShrink: 0
                }}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <CloseIcon style={{ fontSize: '28px' }} /> : <MenuIcon style={{ fontSize: '28px' }} />}
              </button>
            </div>
          </div>
          <div className="mobile-nav">
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', transform: 'scale(0.75)', paddingBottom: '15px', marginRight: '-10px'}}>
              {renderSignUpButton()}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  marginLeft: '5px',
                  width: '44px',
                  height: '44px',
                  minWidth: '44px',
                  minHeight: '44px',
                  flexShrink: 0
                }}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <CloseIcon style={{ fontSize: '28px' }} /> : <MenuIcon style={{ fontSize: '28px' }} />}
              </button>
            </div>
          </div>
        </div>
        
        {/* Navigation Menu (shared for desktop and mobile) - Outside nav-bar to prevent clipping */}
        {isMenuOpen && (
          <div 
            style={{
              position: 'fixed',
              top: '80px',
              right: '20px',
              backgroundColor: 'rgba(9, 2, 19, 0.95)',
              padding: '20px',
              width: '220px',
              borderRadius: '10px',
              backdropFilter: 'blur(10px)',
              zIndex: 10000,
              boxSizing: 'border-box',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              overflow: 'visible'
            }}
          >
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Link
                to="/features"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Features from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Features
              </Link>
              <Link
                to="/how-it-works"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click How It Works from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                How It Works
              </Link>
              <Link
                to="/integrations-and-platforms"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Integrations from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Integrations
              </Link>
              <Link
                to="/solutions-and-accessibility"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Solutions from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Solutions
              </Link>
              <Link
                to="/organizations"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Organizations from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Organizations
              </Link>
              <Link
                to="/api"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click API from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                API
              </Link>
            </div>
          </div>
        )}
        <h1 className="bigger-headline" style={{ marginTop: '60px', paddingTop: '0px' }}>Audio Learning, Built for How You Think</h1>
        <h2 className="subheadline">Audioread provides flexible, screen-free audio learning solutions for individuals and organisations across the UK, adapting to students, professionals, and those seeking accessible audio.</h2>
      </header>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <AutoAwesomeIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>What You Can Achieve with Audioread</h1>
        </div>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Stay informed during tasks where reading isn't possible.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Convert articles, emails, or documents into easy-to-follow audio.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Use AI learning tools for efficient and comfortable content access.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Build daily routines around audio instead of screen time.
          </li>
        </ul>
        <p className="details" style={{ marginTop: '20px', maxWidth: '800px', margin: '20px auto 0' }}>Audioread supports learning, focus, and retention from education to professional development.</p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <WorkIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Productivity That Plays</h1>
        </div>
        <p className="details">Audioread helps with unread tabs and saved articles:</p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Convert reading lists into playlists.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Turn long documents into audio for offline listening.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Utilise "dead" time (commuting, cooking, walking).
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Access private podcast feeds from any app or device.
          </li>
        </ul>
        <p className="details" style={{ marginTop: '20px', maxWidth: '800px', margin: '20px auto 0' }}>These are real-world productivity audio gains, not just features.</p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <AccessibilityIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Designed for Accessibility</h1>
        </div>
        <p className="details">Audioread supports listeners who prefer or need different engagement methods:</p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Ideal for users with dyslexia, ADHD, or visual impairments.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Use browser extensions, mobile shortcuts, or plain copy/paste.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Choose from 80+ languages and AI voices that sound human, not robotic.
          </li>
        </ul>
        <p className="details" style={{ marginTop: '20px', maxWidth: '800px', margin: '20px auto 0' }}>Audioread's commitment to inclusive design makes it enabling, not just adaptable.</p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <PeopleIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>For UK Individuals, Students, Teams</h1>
        </div>
        <p className="details" style={{ marginBottom: '30px', maxWidth: '800px', margin: '0 auto 30px' }}>Audioread works for:</p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Students wanting to revise without reading fatigue.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Busy professionals learning during travel or exercise.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Neurodivergent users needing clarity and control over input.
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Organisations building content accessibility into their learning tools.
          </li>
        </ul>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <h1 className="headline" style={{ marginBottom: '30px' }}>What Our Users Say</h1>
        <Suspense fallback={<div style={{ textAlign: 'center', padding: '40px' }}>Loading testimonials...</div>}>
          <Testimonials />
        </Suspense>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <h1 className="headline">Ready to Listen Smarter?</h1>
        <p className="details">
          Explore how Audioread's <strong>text-to-speech features</strong> help you convert, stream, and learn on your terms.
        </p>
        <div style={{ marginTop: '30px' }}>
          {renderCta()}
        </div>
      </div>

      <div className="footer-links" style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
        <Link to="/terms">Terms of Service</Link>
        <Link to="/privacy">Privacy Policy</Link>
        <Link to="/dmca">DMCA Policy</Link>
        <a href="https://audioread.tolt.io/" target="_blank" rel="noopener noreferrer">Affiliate Program</a>
      </div>
      <div style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
          
      </div>
      <div style={{ margin: '25px', opacity: 0.7 }}>© {new Date().getFullYear()} Audioread™. All rights reserved. | <a href="mailto:info@audioread.com" target="_blank" rel="noopener noreferrer">
          Contact us at info@audioread.com
        </a></div>
    </div>
  );
});

export default LandingSolutions;

